/**
 * Hook for managing storage usage and quota
 *
 * Provides storage usage information and quota tracking.
 */

import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, StorageUsageResponse, FileOperationResponse } from '../api';
import { queryKeys } from '../query-keys';

export interface UseStorageOptions {
  /** Auto-fetch on mount */
  autoFetch?: boolean;
  /** Refresh interval in milliseconds (0 to disable) */
  refreshInterval?: number;
}

export interface UseStorageResult {
  /** Storage usage information */
  usage: StorageUsageResponse | null;
  /** Recent operations */
  operations: FileOperationResponse[];
  /** Whether usage is loading */
  isLoading: boolean;
  /** Error if any */
  error: Error | null;
  /** Percentage of quota used */
  usedPercentage: number;
  /** Whether approaching quota limit (>80%) */
  isApproachingLimit: boolean;
  /** Whether at quota limit (>95%) */
  isAtLimit: boolean;
  /** Formatted total usage */
  totalFormatted: string;
  /** Formatted quota */
  quotaFormatted: string;
  /** Fetch storage usage */
  fetchUsage: () => Promise<void>;
  /** Fetch recent operations */
  fetchOperations: (options?: {
    fileId?: string;
    operationType?: 'upload' | 'download' | 'delete';
    limit?: number;
  }) => Promise<void>;
  /** Refresh all storage data */
  refresh: () => Promise<void>;
}

/**
 * Hook for managing storage usage and quota
 */
export function useStorage(options: UseStorageOptions = {}): UseStorageResult {
  const { autoFetch = true, refreshInterval = 0 } = options;

  // Operations are fetched on-demand with varying params, so keep as local state
  const [operations, setOperations] = useState<FileOperationResponse[]>([]);

  const {
    data: usage,
    isLoading,
    error: usageError,
    refetch: refetchUsage,
  } = useQuery({
    queryKey: queryKeys.storage.usage(),
    queryFn: () => api.getStorageUsage(),
    enabled: autoFetch,
    refetchInterval: refreshInterval > 0 ? refreshInterval : false,
  });

  const { refetch: refetchOperations } = useQuery({
    queryKey: queryKeys.storage.operations(),
    queryFn: async () => {
      const response = await api.listFileOperations({ limit: 20 });
      setOperations(response.operations);
      return response;
    },
    enabled: autoFetch,
    refetchInterval: refreshInterval > 0 ? refreshInterval : false,
  });

  const usedPercentage = usage?.usedPercentage ?? 0;
  const isApproachingLimit = usedPercentage >= 80;
  const isAtLimit = usedPercentage >= 95;
  const totalFormatted = usage?.totalFormatted ?? '0 B';
  const quotaFormatted = usage?.quotaFormatted ?? '0 B';

  const fetchUsage = useCallback(async () => {
    await refetchUsage();
  }, [refetchUsage]);

  const fetchOperations = useCallback(
    async (opts?: {
      fileId?: string;
      operationType?: 'upload' | 'download' | 'delete';
      limit?: number;
    }) => {
      try {
        const response = await api.listFileOperations({
          fileId: opts?.fileId,
          operationType: opts?.operationType,
          limit: opts?.limit ?? 20,
        });
        setOperations(response.operations);
      } catch (err) {
        // Don't overwrite usage error, just log
        console.error('Failed to fetch operations:', err);
      }
    },
    [],
  );

  const refresh = useCallback(async () => {
    await Promise.all([refetchUsage(), refetchOperations()]);
  }, [refetchUsage, refetchOperations]);

  const error =
    usageError instanceof Error
      ? usageError
      : usageError
        ? new Error('Failed to fetch storage usage')
        : null;

  return {
    usage: usage ?? null,
    operations,
    isLoading,
    error,
    usedPercentage,
    isApproachingLimit,
    isAtLimit,
    totalFormatted,
    quotaFormatted,
    fetchUsage,
    fetchOperations,
    refresh,
  };
}

export default useStorage;
